import { format, formatDistanceToNowStrict } from 'date-fns';
import { formatCurrency } from '@/shared/utils';
import {
  AdminBooking,
  AdminCalendarState,
  AdminStylist,
  AdminStylistStatus,
  ConsultationStatus,
  SessionType,
} from '@/shared/types/generated';
import type { Tone } from './AdminUI';

/** Dates come over GraphQL as epoch milliseconds */
export const toDate = (value: unknown) => new Date(value as number | string);

export const ago = (value: unknown) =>
  formatDistanceToNowStrict(toDate(value), { addSuffix: true });

/** "Mar 31", or "Mar 31, 2025" when it isn't this year */
export const shortDate = (value: unknown) => {
  const date = toDate(value);
  return format(
    date,
    date.getFullYear() === new Date().getFullYear() ? 'MMM d' : 'MMM d, yyyy',
  );
};

export const money = (cents?: number | null) =>
  cents === null || cents === undefined ? '—' : formatCurrency(cents);

export const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

/** The viewer's time zone as people say it, e.g. "EDT" */
export const localZoneName = () =>
  new Date()
    .toLocaleTimeString('en-US', { timeZoneName: 'short' })
    .split(' ')
    .pop() ?? '';

// App presence tracking started this day, so "never seen" before it means
// "we don't know", not "never opened the app"
const PRESENCE_TRACKED_SINCE = 'Jul 9';

export const lastInApp = (lastSeenAt: unknown) => {
  if (!lastSeenAt) {
    return `Not since ${PRESENCE_TRACKED_SINCE}`;
  }
  return Date.now() - toDate(lastSeenAt).getTime() < 60_000
    ? 'Just now'
    : ago(lastSeenAt);
};

export const cityName = (city?: string | null) =>
  city
    ? city
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : null;

/** "$240/hr", or null when there's no rate */
export const hourly = (cents?: number | null) =>
  cents ? `${formatCurrency(cents)}/hr` : null;

/** Clients see the in-person option only with all three; say what's missing */
export const inPersonSummary = (stylist: AdminStylist) => {
  if (!stylist.isAvailableInPerson) {
    return null;
  }
  const missing = [
    !stylist.customInPersonHourlyRate && 'a rate',
    !stylist.city && 'an address',
  ].filter(Boolean);
  return missing.length
    ? { text: `In person, needs ${missing.join(' and ')}`, isProblem: true }
    : {
        text: `In person ${hourly(stylist.customInPersonHourlyRate)}`,
        isProblem: false,
      };
};

type StylistState = { label: string; tone: Tone; reason: string };

/** One place that says what's wrong with a stylist and what fixes it */
export const stylistState = (stylist: AdminStylist): StylistState => {
  switch (stylist.status) {
    case AdminStylistStatus.NotListed:
      return {
        label: 'Not listed',
        tone: 'neutral',
        reason: stylist.isTestUser
          ? 'Test account, hidden from clients'
          : 'Hasn’t finished Stripe setup, so clients can’t see them',
      };
    case AdminStylistStatus.NoCalendar:
      if (stylist.calendar === AdminCalendarState.NeedsReconnect) {
        return {
          label: 'Reconnect calendar',
          tone: 'problem',
          reason:
            'Calendar access was revoked or expired. They need to reconnect it in Set Availability.',
        };
      }
      if (stylist.calendar === AdminCalendarState.NoBookingCalendar) {
        return {
          label: 'Pick calendar',
          tone: 'problem',
          reason:
            'Calendar is connected, but they haven’t picked which one bookings go into.',
        };
      }
      return {
        label: 'No calendar',
        tone: 'problem',
        reason: 'Hasn’t connected a calendar in Set Availability yet.',
      };
    case AdminStylistStatus.NoUpcomingHours:
      return {
        label: 'No hours',
        tone: 'attention',
        reason: 'No open hours in the next 4 weeks.',
      };
    default: {
      const days = stylist.upcomingDaysWithHours;
      const next = stylist.nextDateWithHours
        ? ` · next ${format(new Date(`${stylist.nextDateWithHours}T12:00:00`), 'EEE, MMM d')}`
        : '';
      return {
        label: 'Bookable',
        tone: 'neutral',
        reason:
          days > 0
            ? `Open on ${plural(days, 'day')} in the next 4 weeks${next}`
            : 'Open hours set weekly',
      };
    }
  }
};

const isPast = (value: unknown) => toDate(value).getTime() < Date.now();
const DAY_MS = 24 * 60 * 60 * 1000;

export const sessionLabel = (
  booking: Pick<AdminBooking, 'isInstant' | 'sessionType'>,
) => {
  if (booking.isInstant) return 'Instant';
  if (booking.sessionType === SessionType.Video) return 'Virtual';
  return 'In person';
};

/** Only colored when some listed stylists can't be booked */
export const bookableTone = (bookable: number, listed: number): Tone => {
  if (listed === 0 || bookable === listed) return 'neutral';
  return bookable / listed < 0.5 ? 'problem' : 'attention';
};

/** Status in plain words, including rows that were never closed out */
export const bookingStatus = (
  booking: AdminBooking,
): { label: string; tone: Tone } => {
  switch (booking.status) {
    case ConsultationStatus.Upcoming:
      return isPast(booking.startTime)
        ? { label: 'Not closed out', tone: 'attention' }
        : { label: 'Upcoming', tone: 'neutral' };
    case ConsultationStatus.Pending:
      if (booking.isInstant) {
        return isPast(booking.startTime)
          ? { label: 'Instant, unanswered', tone: 'neutral' }
          : { label: 'Instant request', tone: 'attention' };
      }
      return isPast(booking.startTime)
        ? { label: 'Request expired', tone: 'neutral' }
        : { label: 'Needs reply', tone: 'attention' };
    case ConsultationStatus.InProgress:
      // Sessions run a few hours at most
      return Date.now() - toDate(booking.startTime).getTime() > DAY_MS
        ? { label: 'Not closed out', tone: 'attention' }
        : { label: 'In progress', tone: 'good' };
    case ConsultationStatus.Completed:
      return { label: 'Completed', tone: 'good' };
    case ConsultationStatus.Cancelled:
      return { label: 'Canceled', tone: 'neutral' };
    case ConsultationStatus.Declined:
      return { label: 'Declined', tone: 'neutral' };
    case ConsultationStatus.ClientNoShow:
      return { label: 'Client no-show', tone: 'problem' };
    case ConsultationStatus.StylistNoShow:
      return { label: 'Stylist no-show', tone: 'problem' };
    default:
      return { label: String(booking.status), tone: 'neutral' };
  }
};

const STILL_HELD = [
  ConsultationStatus.Upcoming,
  ConsultationStatus.Pending,
  ConsultationStatus.InProgress,
];

/** What happened with the money, without Stripe's wording */
export const paymentSummary = (booking: AdminBooking) => {
  if (booking.capturedCents) {
    return `Charged ${money(booking.capturedCents)}`;
  }
  if (!booking.authorizedCents) {
    return 'No payment';
  }
  // Going by the booking's status; a hold on a row that was never closed out
  // may since have lapsed at Stripe
  return STILL_HELD.includes(booking.status)
    ? `Card held ${money(booking.authorizedCents)}`
    : 'Hold released';
};
